import axios from 'axios'
import * as AloAi from '../../constants/aloai'

export default {
    namespaced: true,

    state: {
        // Tools
        tools: [],
        toolsLoading: false,

        // Voice-related state
        voices: [],
        voiceModels: [],
        languages: [],
        voicesLoading: false,
        voiceModelsLoading: false,
        languagesLoading: false
    },

    getters: {
        // Tools getters
        getTools: state => {
            return state.tools || []
        },

        getToolsLoading: state => {
            return state.toolsLoading || false
        },

        // Voice-related getters
        getVoices: state => {
            return state.voices || []
        },

        getVoiceModels: state => {
            return state.voiceModels || []
        },

        getLanguages: state => {
            return state.languages || []
        },

        getVoicesLoading: state => {
            return state.voicesLoading || false
        },

        getVoiceModelsLoading: state => {
            return state.voiceModelsLoading || false
        },

        getLanguagesLoading: state => {
            return state.languagesLoading || false
        },

        getFilteredLanguages: (state) => (botVoiceId) => {
            if (!state.voices || !state.languages) {
                return []
            }

            const selectedVoice = state.voices.find(voice => voice.id === botVoiceId)
            let filteredLanguages = state.languages

            // If using a Deepgram Voice, languages must only show en-US and en-GB
            if (selectedVoice && selectedVoice.provider === AloAi.PROVIDER_DEEPGRAM) {
                filteredLanguages = filteredLanguages.filter(language => {
                    return language.code === AloAi.LANGUAGE_EN_US || language.code === AloAi.LANGUAGE_EN_GB
                })
            }

            return filteredLanguages
        },

        getFilteredVoices: (state) => (botVoiceModel) => {
            if (!state.voices || !Array.isArray(state.voices)) {
                return []
            }

            let voicesClone = [...state.voices]

            // If using OpenAI Realtime model, show only compatible OpenAI voices
            if (voicesClone.length > 0 && (botVoiceModel === AloAi.VOICE_MODEL_GPT_REALTIME_15 || botVoiceModel === AloAi.VOICE_MODEL_GPT_REALTIME_MINI)) {
                voicesClone = voicesClone.filter(voice => {
                    return voice.provider === AloAi.PROVIDER_OPENAI && !AloAi.UNSUPPORTED_S2S_VOICES.includes(voice.external_id)
                })
            }

            return voicesClone
        },

        getGroupedVoices: (state, getters) => (botVoiceModel) => {
            const voices = getters.getFilteredVoices(botVoiceModel)

            // Initialize groups for ElevenLabs and OpenAI
            const groups = {
                [AloAi.PROVIDER_ELEVENLABS]: [],
                [AloAi.PROVIDER_OPENAI]: []
            }

            if (Array.isArray(voices)) {
                voices.forEach(voice => {
                    if (groups.hasOwnProperty(voice.provider)) {
                        groups[voice.provider].push(voice)
                    }
                })
            }

            // Return groups with proper labels
            return [
                { label: AloAi.LABEL_ELEVENLABS, voices: groups[AloAi.PROVIDER_ELEVENLABS] },
                { label: AloAi.LABEL_OPENAI, voices: groups[AloAi.PROVIDER_OPENAI] }
            ]
        },

        getGroupedVoiceModels: (state) => {
            if (!state.voiceModels || !Array.isArray(state.voiceModels)) {
                return []
            }

            // Initialize groups for each provider
            const groups = {
                [AloAi.PROVIDER_ANTHROPIC]: [],
                [AloAi.PROVIDER_GOOGLE]: [],
                [AloAi.PROVIDER_OPENAI]: [],
                [AloAi.GROUP_OPENAI_REALTIME]: []
            }

            // Group models by provider
            state.voiceModels.forEach(model => {
                if (groups.hasOwnProperty(model.provider)) {
                    groups[model.provider].push(model)
                }
            })

            // Return groups with proper labels
            return [
                { label: AloAi.LABEL_ANTHROPIC, models: groups[AloAi.PROVIDER_ANTHROPIC] },
                { label: AloAi.LABEL_GOOGLE, models: groups[AloAi.PROVIDER_GOOGLE] },
                { label: AloAi.LABEL_OPENAI, models: groups[AloAi.PROVIDER_OPENAI] },
                { label: AloAi.LABEL_OPENAI_REALTIME, models: groups[AloAi.GROUP_OPENAI_REALTIME] }
            ].filter(group => group.models.length > 0)
        },

        // Voice compatibility helpers
        isRealtimeModel: (state) => (voiceModel) => {
            return voiceModel === AloAi.VOICE_MODEL_GPT_REALTIME_15 || voiceModel === AloAi.VOICE_MODEL_GPT_REALTIME_MINI
        },

        isVoiceCompatibleWithModel: (state, getters) => (voiceId, voiceModel) => {
            if (!state.voices || !Array.isArray(state.voices)) {
                return false
            }

            const voice = state.voices.find(v => v.id === voiceId)
            if (!voice) return false

            // If using realtime model, voice must be OpenAI and not in unsupported list
            if (getters.isRealtimeModel(voiceModel)) {
                return voice.provider === AloAi.PROVIDER_OPENAI && !AloAi.UNSUPPORTED_S2S_VOICES.includes(voice.external_id)
            }

            return true
        },

        getDefaultVoiceForModel: (state, getters) => (voiceModel) => {
            const filteredVoices = getters.getFilteredVoices(voiceModel)

            if (!filteredVoices || filteredVoices.length === 0) return null

            // If using realtime model, try to find the default OpenAI voice
            if (getters.isRealtimeModel(voiceModel)) {
                const defaultVoice = filteredVoices.find(voice =>
                    voice.external_id === AloAi.DEFAULT_OPENAI_VOICE_ID
                )
                return defaultVoice || filteredVoices[0]
            }

            const defaultVoice = filteredVoices.find(voice =>
                voice.external_id === AloAi.DEFAULT_11LABS_VOICE_ID
            )
            return defaultVoice || filteredVoices[0]
        }
    },

    mutations: {
        // Tools mutations
        SET_TOOLS(state, tools) {
            state.tools = tools
        },

        SET_TOOLS_LOADING(state, loading) {
            state.toolsLoading = loading
        },

        // Voice-related mutations
        SET_VOICES(state, voices) {
            state.voices = voices
        },

        SET_VOICE_MODELS(state, voiceModels) {
            state.voiceModels = voiceModels
        },

        SET_LANGUAGES(state, languages) {
            state.languages = languages
        },

        SET_VOICES_LOADING(state, loading) {
            state.voicesLoading = loading
        },

        SET_VOICE_MODELS_LOADING(state, loading) {
            state.voiceModelsLoading = loading
        },

        SET_LANGUAGES_LOADING(state, loading) {
            state.languagesLoading = loading
        }
    },

    actions: {
        // Tools actions
        fetchTools({ commit, state }, force = false) {
            // Check if tools are already loaded (unless force is true)
            if (!force && state.tools && state.tools.length > 0 && !state.toolsLoading) {
                return Promise.resolve(state.tools)
            }

            commit('SET_TOOLS_LOADING', true)

            return axios.get('/api/v1/aloai/tools')
                .then((response) => {
                    commit('SET_TOOLS', response.data.data)
                    return response.data.data
                }).catch((error) => {
                    console.error('Error fetching tools:', error)
                    throw error
                }).finally(() => {
                    commit('SET_TOOLS_LOADING', false)
                })
        },

        setTools({ commit }, tools) {
            commit('SET_TOOLS', tools)
        },

        // Voice-related actions
        fetchVoices({ commit, state }) {
            // Check if voices are already loaded
            if (state.voices && state.voices.length > 0 && !state.voicesLoading) {
                return Promise.resolve(state.voices)
            }

            commit('SET_VOICES_LOADING', true)

            return axios.get('/api/v1/aloai/bot/voice/voices')
                .then((response) => {
                    commit('SET_VOICES', response.data.data)
                    return response.data.data
                })
                .catch((error) => {
                    console.error('Error fetching voices:', error)
                })
                .finally(() => {
                    commit('SET_VOICES_LOADING', false)
                })
        },

        fetchVoiceModels({ commit, state }) {
            // Check if voice models are already loaded
            if (state.voiceModels && state.voiceModels.length > 0 && !state.voiceModelsLoading) {
                return Promise.resolve(state.voiceModels)
            }

            commit('SET_VOICE_MODELS_LOADING', true)

            return axios.get('/api/v1/aloai/bot/voice/models')
                .then((response) => {
                    commit('SET_VOICE_MODELS', response.data)
                    return response.data
                })
                .catch((error) => {
                    console.error('Error fetching voice models:', error)
                })
                .finally(() => {
                    commit('SET_VOICE_MODELS_LOADING', false)
                })
        },

        fetchLanguages({ commit, state }) {
            // Check if languages are already loaded
            if (state.languages && state.languages.length > 0 && !state.languagesLoading) {
                return Promise.resolve(state.languages)
            }

            commit('SET_LANGUAGES_LOADING', true)

            return axios.get('/api/v1/aloai/bot/voice/languages')
                .then((response) => {
                    commit('SET_LANGUAGES', response.data)
                    return response.data
                })
                .catch((error) => {
                    console.error('Error fetching languages:', error)
                })
                .finally(() => {
                    commit('SET_LANGUAGES_LOADING', false)
                })
        },

        // Force refresh voice data (bypasses cache)
        refreshVoices({ commit, dispatch }) {
            commit('SET_VOICES', [])
            return dispatch('fetchVoices')
        },

        refreshVoiceModels({ commit, dispatch }) {
            commit('SET_VOICE_MODELS', [])
            return dispatch('fetchVoiceModels')
        },

        refreshLanguages({ commit, dispatch }) {
            commit('SET_LANGUAGES', [])
            return dispatch('fetchLanguages')
        },

        // Batch fetch all voice data
        fetchAllVoiceData({ dispatch }) {
            return Promise.all([
                dispatch('fetchVoices'),
                dispatch('fetchVoiceModels'),
                dispatch('fetchLanguages')
            ])
        },

        setVoices({ commit }, voices) {
            commit('SET_VOICES', voices)
        },

        setVoiceModels({ commit }, voiceModels) {
            commit('SET_VOICE_MODELS', voiceModels)
        },

        setLanguages({ commit }, languages) {
            commit('SET_LANGUAGES', languages)
        }
    }
}
